"use client"

import { useEffect, useState } from "react"
import {
  TrendingUp,
  Filter,
  Search,
  ChevronDown,
  Clock,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Calendar,
  Percent,
  Users,
  ArrowUpRight,
  Sliders,
  RefreshCw,
  BarChart2,
} from "lucide-react"
import WrapUpP2p from "./layouts/WrapUpP2p"
import { exchangeActions } from "../../../redux/actions"
import { useDispatch, useSelector } from "react-redux"
import { exchangeSelect } from "../../../redux/selectors"
import { formatDateTime, money, notify } from "../../../utility/Helper1"
import { useNavigate } from "react-router-dom"

const InvestmentListPage = () => {

  // Filter states
  const [activeFilter, setActiveFilter] = useState("All")
  const [searchQuery, setSearchQuery] = useState("")
  const [sortBy, setSortBy] = useState("interestRate")
  const [sortOrder, setSortOrder] = useState("desc")
  const [loadingData, setLoadingData] = useState(false)
  const exchangeData = useSelector(exchangeSelect);
  const investments = exchangeData?.filter(el => Number(el.asset) == 1)
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const onLoadPH = () => {

    setLoadingData(true)
    dispatch(exchangeActions.onGetExchange("p2p/load/all", response => {
      setLoadingData(false)
    }))

  }

  useEffect(() => {
    onLoadPH()
  }, [])


  const deletePH = (objToDelete) => {

    dispatch(exchangeActions.onGetExchange(`p2p/exchange/delete/${objToDelete?.id}`, response => {
      notify(response.msg, response.code)
    }))

  }

  // Filter options
  const filterOptions = ["All", "Peered", "Reinvested", "Matured"]

  // Filter investments based on active filter and search query
  const filteredInvestments = investments?.filter((investment) => {

    // Category filter
    if (activeFilter === "All") return true
    if (activeFilter === "Peered" && investment.matchedph > 0) return true
    if (activeFilter === "Reinvested" && investment.recommitted == 1) return true
    if (activeFilter === "Matured" && investment.phready == "NO") return true

    return false
  })

  // Sort investments
  const sortedInvestments = [...(filteredInvestments ?? [])].sort((a, b) => {
    if (sortOrder === "asc") {
      return a[sortBy] - b[sortBy]
    } else {
      return b[sortBy] - a[sortBy]
    }
  })


  // Get risk level color
  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case "1":
        return "bg-green-100 text-green-800"
      case "Medium":
        return "bg-yellow-100 text-yellow-800"
      case "0":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const getElapsedPercentage = (phpTimestamp, days) => {
    if (!phpTimestamp || !days) return 0;

    const start = phpTimestamp * 1000; // Convert from seconds to milliseconds
    const end = start + (days * 24 * 60 * 60 * 1000); // Add days
    const now = Date.now();

    if (now >= end) return 100;

    const elapsed = now - start;
    const total = end - start;
    const percent = (elapsed / total) * 100;

    return Math.min(Math.max(percent, 0), 100);
  }


  return (<WrapUpP2p>
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Investment Opportunities</h1>
            <p className="mt-1 text-sm text-gray-600">Discover and invest in peer-to-peer lending opportunities</p>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-3">
            <button onClick={() => navigate('/projects/new')} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
              <TrendingUp size={16} className="mr-2" />
              Add
            </button>
            <button onClick={onLoadPH} className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700 flex items-center">
                <Filter size={16} className="mr-1" /> Filter:
              </span>
              {filterOptions.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-3 py-1 text-sm rounded-full ${activeFilter === filter ? "bg-green-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  {filter}
                </button>
              ))}
            </div>

          </div>

          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-500">
              Showing <span className="font-medium">{sortedInvestments.length}</span> investment opportunities
            </div>
          </div>
        </div>


        <div className="bg-white overflow-hidden shadow-md rounded-xl border border-gray-100">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Investment
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Amount
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Dispatchable
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Term
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Recommitment
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Maturity
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedInvestments.map((investment) => {
                  let fundingPercentage = getElapsedPercentage(investment?.timephconf2, investment?.ghday)

                  if (investment?.timephconf2 == 0) {
                    fundingPercentage = 0
                  }

                  let deletable = investment.lastph == investment.ph && investment.phready == 'YES'

                  return (
                    <tr key={investment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {/* <div className="flex-shrink-0 h-10 w-10">
                            <img
                              className="h-10 w-10 rounded-md"
                              src={investment.imgUrl || "/placeholder.svg"}
                              alt=""
                            />
                          </div> */}
                          <div className="ml-0">
                            <div className="text-sm font-medium text-gray-900">{investment.transid}</div>
                            <div className="text-sm text-gray-500">{formatDateTime(Number(investment.phtimestamp2))}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{money(investment.lastph, investment.motherType)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-green-600">{money(investment.ph, investment.motherType)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{investment.ghday} days</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRiskLevelColor(investment.recommitted)}`}
                        >
                          {investment.recommitted == 1 ? "✅" : "❌"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-full max-w-xs">
                          {investment?.timephconf2 != 0 && <div className="flex justify-between text-xs mb-1">
                            <span>{formatDateTime(Number(investment.timephconf2) + (Number(investment.ghday) * 24 * 60 * 60))}</span>
                          </div>}
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-green-600 h-1.5 rounded-full"
                              style={{ width: `${fundingPercentage}%` }}
                            ></div>
                          </div>
                          {investment?.timephconf2 != 0 && <div className="flex justify-between text-xs mb-1">
                            <span>{Math.round(fundingPercentage)}%</span>
                          </div>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button

                          onClick={() => deletable && window.confirm('Permanently Delete?') && deletePH(investment)}
                          className={`inline-flex items-center px-3 py-1 border border-transparent rounded-md text-xs font-medium ${!deletable
                            ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                            : "text-white bg-green-600 hover:bg-green-700"
                            }`}
                          disabled={!deletable}
                        >
                          {"Delete"}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  </WrapUpP2p>
  )
}

export default InvestmentListPage
